import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { TbTrash, TbArrowRight } from "react-icons/tb";
import Popup from "src/components/Popup/Popup";
import { apiRequest } from "src/services/apiClient";
import { CREATE_STEPS } from "src/pages/InvoiceCreate/createSteps";
import "./invoiceDraftsPopup.scss";

export interface InvoiceDraftSummary {
    id: string;
    step: number;
    updatedAt: string;
    summary: { patientName: string; total: number; lineCount: number };
}

const fmtEuro = (n: number) => n.toLocaleString("de-DE", { style: "currency", currency: "EUR" });
const fmtWhen = (iso: string) =>
    new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

/** The member's unfinished manual invoices (chapter 18, story 9) — continue or delete. */
export function InvoiceDraftsPopup({ drafts, onClose, onChanged }: { drafts: InvoiceDraftSummary[]; onClose: () => void; onChanged: () => void }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [deleting, setDeleting] = useState<string | null>(null);

    const remove = async (id: string) => {
        setDeleting(id);
        try {
            await apiRequest(`/invoice-drafts/${id}`, { method: "DELETE" });
        } finally {
            setDeleting(null);
            onChanged();
        }
    };

    return (
        <Popup
            title={t("invoiceDrafts.title")}
            onClose={onClose}
            actions={[{ label: t("popup.close", { defaultValue: "Schließen" }), variant: "default", onClick: onClose }]}
            message={
                drafts.length === 0 ? (
                    <p className="invoice-drafts__empty">{t("invoiceDrafts.empty")}</p>
                ) : (
                    <ul className="invoice-drafts">
                        {drafts.map((d) => {
                            const step = CREATE_STEPS[Math.min(d.step, CREATE_STEPS.length - 1)]?.key ?? "patient";
                            return (
                                <li key={d.id} className="invoice-drafts__item">
                                    <div className="invoice-drafts__text">
                                        <span className="invoice-drafts__name">{d.summary.patientName || t("invoiceDrafts.noPatient")}</span>
                                        <span className="invoice-drafts__meta">
                                            {[
                                                d.summary.lineCount > 0 ? `${t("invoiceCreate.services.summaryLines", { count: d.summary.lineCount })} · ${fmtEuro(d.summary.total)}` : null,
                                                t("invoiceDrafts.atStep", { step: t(`invoiceCreate.steps.${step}`) }),
                                                t("invoiceDrafts.updated", { when: fmtWhen(d.updatedAt) }),
                                            ].filter(Boolean).join(" · ")}
                                        </span>
                                    </div>
                                    <button
                                        type="button"
                                        className="invoice-drafts__delete"
                                        onClick={() => void remove(d.id)}
                                        disabled={deleting === d.id}
                                        aria-label={t("invoiceDrafts.delete", { name: d.summary.patientName || t("invoiceDrafts.noPatient") })}
                                        title={t("invoiceDrafts.delete", { name: d.summary.patientName || t("invoiceDrafts.noPatient") })}
                                    >
                                        <TbTrash aria-hidden />
                                    </button>
                                    <button type="button" className="invoice-drafts__continue" onClick={() => navigate(`/invoices/new?draft=${d.id}`)}>
                                        {t("invoiceDrafts.continue")} <TbArrowRight aria-hidden />
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                )
            }
        />
    );
}
